/** Batch "Edit display settings" dialog for the Itineraries grid (a custom
 *  `dialog(ctx)` batch action).
 *
 *  Applies the catalog display settings the itinerary editor exposes on
 *  Itinerary_Group. Each toggle is tri-state: "Leave unchanged" (omitted from the
 *  payload) vs an explicit on/off — so a batch edit only touches the fields the
 *  operator actually set. Not cross-tenant: each group is edited in place.
 *  Submit fires the generic `/itineraries/batch/` dispatch via `ctx.runStandard`.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

/** The Itinerary_Group boolean display-setting fields the backend handler accepts. */
const BOOLEAN_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'show_in_catalog', label: 'Show in catalog' },
  { key: 'show_in_trip_type_search', label: 'Show in trip-type search' },
  { key: 'show_in_region_search', label: 'Show in region search' },
  { key: 'show_in_text_search', label: 'Show in free-text search' },
  { key: 'custom_day_titles', label: 'Show day titles instead of numbers' },
];

/** Tri-state select value: leave unchanged / set true / set false. */
type TriState = 'unchanged' | 'true' | 'false';

interface Props {
  ctx: BatchActionContext;
}

export function EditDisplaySettingsDialog({ ctx }: Props) {
  const [bools, setBools] = useState<Record<string, TriState>>(() =>
    Object.fromEntries(BOOLEAN_FIELDS.map((f) => [f.key, 'unchanged' as TriState])),
  );
  // duration_display: unchecked → left unchanged; checked → set to the text (may be empty → cleared).
  const [durationEnabled, setDurationEnabled] = useState(false);
  const [duration, setDuration] = useState('');

  const buildParams = (): Record<string, unknown> => {
    const params: Record<string, unknown> = {};
    for (const f of BOOLEAN_FIELDS) {
      const v = bools[f.key];
      if (v === 'true') params[f.key] = true;
      else if (v === 'false') params[f.key] = false;
    }
    if (durationEnabled) params.duration_display = duration.trim() || null;
    return params;
  };

  const params = buildParams();
  const canSubmit = Object.keys(params).length > 0;

  const submit = () => {
    void ctx.runStandard(params);
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Edit display settings</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Update display settings for {ctx.count.toLocaleString()} itiner
            {ctx.count === 1 ? 'ary' : 'aries'}. Fields left as “Leave unchanged” are not touched.
            Runs in the background.
          </Typography>

          {BOOLEAN_FIELDS.map((f) => (
            <TextField
              key={f.key}
              select
              size="small"
              label={f.label}
              value={bools[f.key] ?? 'unchanged'}
              onChange={(e) =>
                setBools((prev) => ({ ...prev, [f.key]: e.target.value as TriState }))
              }
            >
              <MenuItem value="unchanged">Leave unchanged</MenuItem>
              <MenuItem value="true">On</MenuItem>
              <MenuItem value="false">Off</MenuItem>
            </TextField>
          ))}

          <TextField
            select
            size="small"
            label="Duration display"
            value={durationEnabled ? 'set' : 'unchanged'}
            onChange={(e) => setDurationEnabled(e.target.value === 'set')}
          >
            <MenuItem value="unchanged">Leave unchanged</MenuItem>
            <MenuItem value="set">Set to…</MenuItem>
          </TextField>
          {durationEnabled && (
            <TextField
              size="small"
              label="Duration text"
              placeholder="e.g. 3–4 days (empty clears it)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} itineraries.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
